import {Injectable} from "@angular/core";
import {CompetitorService} from "./competitor.service";
import {LocalStorageService} from "./local-storage.service";
import { CompetitorSaveControlPointResultStatus, ControlPointTime, MeasurementTimeItemHolder, WaypointType} from "@model";
import {catchError, forkJoin, Observable, of, switchMap} from 'rxjs';
import {map} from 'rxjs/operators';
import {v4 as uuidv4} from "uuid";
import {MeasurementsService} from "@services";

@Injectable({providedIn: 'root'})
export class MeasurementTimeItemHolderService {

    constructor(
        private competitorService: CompetitorService,
        private localStorageService: LocalStorageService,
        private measurementsService: MeasurementsService
    ) {
    }


    private createKey = (measurementsPointsView: boolean, eventId: number) => `event-${measurementsPointsView ? 'checkpoints' : 'laps'}-${eventId}`;


    saveHolderForEvent(measurementsPointsView: boolean, eventId: number, data: string) {
        this.localStorageService.saveData(
            this.createKey(measurementsPointsView, eventId), data
        );
    }

    deleteHolderForEvent(measurementsPointsView: boolean, eventId: number) {
        this.localStorageService.removeData(this.createKey(measurementsPointsView, eventId));
    }

    getHolderForEvent(measurementsPointsView: boolean, eventId: number, update?: boolean): Observable<MeasurementTimeItemHolder[]> {
        const value = this.localStorageService.getData(this.createKey(measurementsPointsView, eventId));
        if (!value) return of([]);

        const measurementTimeItemHolders: MeasurementTimeItemHolder[] = JSON.parse(value);
        if (!update) return of(measurementTimeItemHolders);

        const uniqueCompetitorIds = Array.from(
            new Set(measurementTimeItemHolders
                .filter(holder => holder.competitor?.id)
                .map(holder => holder.competitor!.id!))
        );

        if (uniqueCompetitorIds.length === 0) {
            return of(measurementTimeItemHolders);
        }

        return forkJoin(uniqueCompetitorIds.map(id =>
            this.competitorService.fetchCompetitorById(id).pipe(
                switchMap(updatedCompetitor => {
                    return !measurementsPointsView && updatedCompetitor?.raceId
                        ? this.measurementsService.findLapsTimes(id, updatedCompetitor.raceId).pipe(
                            map((laps:ControlPointTime[])  => {
                                const filteredLaps = laps.filter(lap => !(lap.controlWaypoint && lap.controlWaypoint.waypointTypeId === WaypointType.START_WAYPOINT));
                                return { id, updatedCompetitor: { ...updatedCompetitor, laps: filteredLaps } };
                            }
                        ))
                        : of({ id, updatedCompetitor });
                }),
                catchError(() => of({ id, updatedCompetitor: null }))
            )
        )).pipe(
            map(competitorUpdates => {
                const competitorMap = new Map(competitorUpdates
                    .filter(({ updatedCompetitor }) => updatedCompetitor)
                    .map(({ id, updatedCompetitor }) => [id, updatedCompetitor!])
                );

                return measurementTimeItemHolders.map(holder => {
                    const updatedCompetitor = holder.competitor?.id ? competitorMap.get(holder.competitor.id) : holder.competitor;
                    return {
                        ...holder,
                        key: uuidv4(),
                        saveResult: CompetitorSaveControlPointResultStatus.OK,
                        competitor: updatedCompetitor,
                        startNumber: updatedCompetitor?.startNumber ?? holder.competitor?.startNumber,
                    };
                });
            })
        );
    }

}


